import React, { useCallback, useEffect, useRef, useState } from 'react';
import { X, Trash2, Loader2, EyeOff } from 'lucide-react';
import Button from '../ui/Button';
import appConfig from '../../config/appConfig';

/*
  Covers regions of a photo with the site name BEFORE it is saved.

  The clean original never leaves the device. Blurring through a Cloudinary
  delivery transformation would not achieve that — those live in the URL, so
  anyone can strip the transformation and fetch the untouched image. Here the
  pixels are rewritten in a canvas and the File handed back is the only one that
  reaches the server.

  Aimed by hand rather than detected: a plate at an angle, in shade, or half out
  of frame defeats OCR, and silently missing one is worse than not offering the
  feature — the seller would believe their plate was covered when it wasn't.
*/

// Taken from config rather than written in, so a rebrand or a white-label
// deployment does not leave the old domain burned into every photo from then on.
const LABEL = String(appConfig.contact?.website || appConfig.name || '').replace(/^www\./i, '');

const MIN_BOX = 8;          // natural px; anything smaller is a stray tap
const CORNERS = ['nw', 'ne', 'sw', 'se'];

// Build a rect from two opposite corners, always with positive width/height.
const fromCorners = (a, b) => ({
  x: Math.min(a.x, b.x), y: Math.min(a.y, b.y),
  w: Math.abs(b.x - a.x), h: Math.abs(b.y - a.y)
});
const cornerPoint = (r, c) => ({
  x: c === 'nw' || c === 'sw' ? r.x : r.x + r.w,
  y: c === 'nw' || c === 'ne' ? r.y : r.y + r.h
});
const opposite = { nw: 'se', ne: 'sw', sw: 'ne', se: 'nw' };

export default function PlateBlurModal({ file, src, name, onCancel, onApply }) {
  const canvasRef = useRef(null);
  const imgRef = useRef(null);
  const [rects, setRects] = useState([]);
  const [selected, setSelected] = useState(null);
  const [ready, setReady] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const gesture = useRef(null);   // { type: 'resize'|'move', anchor?, corner?, dx?, dy? }

  useEffect(() => {
    // `cancelled` matters: StrictMode runs this effect twice in development and
    // the cleanup revokes the blob URL while the first Image is still loading.
    // That image then fires onerror for a load nobody is waiting on any more.
    let cancelled = false;
    setReady(false);
    setError('');

    const objectUrl = file ? URL.createObjectURL(file) : null;
    const img = new Image();
    if (!file) img.crossOrigin = 'anonymous';   // Cloudinary sends ACAO:*, so the canvas stays untainted
    img.onload = () => { if (!cancelled) { imgRef.current = img; setReady(true); } };
    img.onerror = () => { if (!cancelled) setError('That photo could not be opened for editing.'); };
    img.src = objectUrl || src;

    return () => { cancelled = true; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [file, src]);

  // Cover the region with the site name: the plate stops being readable, and the
  // photo carries the brand if it is lifted for a listing elsewhere. Solid, not
  // translucent — anything see-through leaves the characters legible underneath.
  const stamp = useCallback((ctx, r) => {
    if (r.w < 4 || r.h < 4) return;
    const unit = ctx.canvas.width / 400;      // scale strokes with the image, not the screen
    ctx.save();
    ctx.fillStyle = '#111827';
    ctx.fillRect(r.x, r.y, r.w, r.h);

    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = Math.max(2, unit * 1.2);
    ctx.strokeRect(r.x + ctx.lineWidth / 2, r.y + ctx.lineWidth / 2,
                   r.w - ctx.lineWidth, r.h - ctx.lineWidth);

    if (LABEL) {
      // Fit the text to the box: measure once at a reference size, then scale. A
      // plate is wide and short so width normally wins, but height caps a tall box.
      const pad = Math.min(r.w, r.h) * 0.18;
      const REF = 100;
      ctx.font = `700 ${REF}px Arial, Helvetica, sans-serif`;
      const refWidth = ctx.measureText(LABEL).width || 1;
      const size = Math.max(8, Math.min(((r.w - pad * 2) / refWidth) * REF, r.h - pad * 2));
      ctx.font = `700 ${size}px Arial, Helvetica, sans-serif`;
      ctx.fillStyle = '#ffffff';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(LABEL, r.x + r.w / 2, r.y + r.h / 2);
    }
    ctx.restore();
  }, []);

  const handleSize = () => Math.max(10, (canvasRef.current?.width || 1000) / 70);

  // Handles are UI, not part of the photo — `withHandles` is false when exporting.
  const paint = useCallback((withHandles) => {
    const img = imgRef.current, canvas = canvasRef.current;
    if (!img || !canvas) return;
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(img, 0, 0);
    rects.forEach((r) => stamp(ctx, r));

    if (withHandles && selected !== null && rects[selected]) {
      const r = rects[selected];
      const hs = handleSize();
      ctx.save();
      ctx.strokeStyle = '#2563eb';
      ctx.lineWidth = Math.max(2, canvas.width / 500);
      ctx.setLineDash([ctx.lineWidth * 3, ctx.lineWidth * 2]);
      ctx.strokeRect(r.x, r.y, r.w, r.h);
      ctx.setLineDash([]);
      CORNERS.forEach((c) => {
        const p = cornerPoint(r, c);
        ctx.fillStyle = '#ffffff';
        ctx.strokeStyle = '#2563eb';
        ctx.lineWidth = Math.max(1.5, canvas.width / 800);
        ctx.fillRect(p.x - hs / 2, p.y - hs / 2, hs, hs);
        ctx.strokeRect(p.x - hs / 2, p.y - hs / 2, hs, hs);
      });
      ctx.restore();
    }
  }, [rects, selected, stamp]);

  useEffect(() => { if (ready) paint(true); }, [ready, paint]);

  // Pointer position -> natural image coordinates. The canvas is displayed
  // scaled down, so a click 300px across the screen is not 300px into the image.
  const toNatural = (e) => {
    const canvas = canvasRef.current;
    const box = canvas.getBoundingClientRect();
    const p = e.touches && e.touches[0] ? e.touches[0] : e;
    return {
      x: ((p.clientX - box.left) / box.width) * canvas.width,
      y: ((p.clientY - box.top) / box.height) * canvas.height
    };
  };

  const start = (e) => {
    e.preventDefault();
    const p = toNatural(e);
    const tol = handleSize();

    // 1. a corner of the selected box -> resize from the opposite corner
    if (selected !== null && rects[selected]) {
      const r = rects[selected];
      const hit = CORNERS.find((c) => {
        const cp = cornerPoint(r, c);
        return Math.abs(p.x - cp.x) <= tol && Math.abs(p.y - cp.y) <= tol;
      });
      if (hit) {
        gesture.current = { type: 'resize', anchor: cornerPoint(r, opposite[hit]) };
        return;
      }
    }

    // 2. inside an existing box (topmost first) -> select and move it
    for (let i = rects.length - 1; i >= 0; i--) {
      const r = rects[i];
      if (p.x >= r.x && p.x <= r.x + r.w && p.y >= r.y && p.y <= r.y + r.h) {
        setSelected(i);
        gesture.current = { type: 'move', index: i, dx: p.x - r.x, dy: p.y - r.y };
        return;
      }
    }

    // 3. empty space -> draw a new box, which is just a resize from where you pressed
    setRects((prev) => {
      const next = [...prev, { x: p.x, y: p.y, w: 0, h: 0 }];
      setSelected(next.length - 1);
      return next;
    });
    gesture.current = { type: 'resize', anchor: p, fresh: true };
  };

  const move = (e) => {
    const g = gesture.current;
    if (!g) return;
    e.preventDefault();
    const p = toNatural(e);
    setRects((prev) => prev.map((r, i) => {
      const idx = g.type === 'move' ? g.index : (selected ?? prev.length - 1);
      if (i !== idx) return r;
      if (g.type === 'resize') return fromCorners(g.anchor, p);
      const canvas = canvasRef.current;
      return {
        ...r,
        x: Math.max(0, Math.min(canvas.width - r.w, p.x - g.dx)),
        y: Math.max(0, Math.min(canvas.height - r.h, p.y - g.dy))
      };
    }));
  };

  const end = () => {
    if (!gesture.current) return;
    gesture.current = null;
    // Drop a stray tap, but never an existing box the user merely clicked.
    setRects((prev) => {
      const next = prev.filter((r) => r.w >= MIN_BOX && r.h >= MIN_BOX);
      if (next.length !== prev.length) setSelected(next.length ? next.length - 1 : null);
      return next;
    });
  };

  const removeSelected = () => {
    setRects((prev) => prev.filter((_, i) => i !== selected));
    setSelected(null);
  };

  const apply = () => {
    setSaving(true);
    paint(false);   // drop the selection outline and handles before exporting
    canvasRef.current.toBlob(async (blob) => {
      const fileName = (file && file.name) || name || 'photo.jpg';
      try {
        await onApply(new File([blob], fileName, { type: blob.type, lastModified: Date.now() }));
      } catch (err) {
        setError('Could not save the edited photo. Please try again.');
        setSaving(false);
        paint(true);
      }
    }, 'image/jpeg', 0.9);
  };

  return (
    <div className="fixed inset-0 z-[100] flex flex-col bg-black/85">
      <div className="flex items-center justify-between px-4 py-3 text-white">
        <div className="flex items-center gap-2">
          <EyeOff className="h-5 w-5" />
          <span className="font-semibold">Hide the number plate</span>
        </div>
        <button type="button" onClick={onCancel} className="p-1 rounded hover:bg-white/10">
          <X className="h-5 w-5" />
        </button>
      </div>

      <p className="px-4 pb-3 text-sm text-white/80">
        Drag a box over the number plate — it is covered with <strong>{LABEL}</strong>.
        Drag inside a box to move it, or a corner to resize. The photo is changed before
        it is saved, so the unedited original is not kept.
      </p>

      <div className="flex-1 overflow-auto px-4 flex items-center justify-center min-h-0">
        {error ? (
          <p className="text-sm text-red-300 text-center">{error}</p>
        ) : !ready ? (
          <Loader2 className="h-8 w-8 text-white animate-spin" />
        ) : (
          <canvas
            ref={canvasRef}
            onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
            onTouchStart={start} onTouchMove={move} onTouchEnd={end}
            className="max-w-full max-h-full object-contain touch-none cursor-crosshair rounded"
          />
        )}
      </div>

      <div className="flex items-center justify-between gap-3 px-4 py-4">
        <button
          type="button"
          onClick={removeSelected}
          disabled={selected === null}
          className="flex items-center gap-1.5 text-sm font-medium text-white/90 disabled:opacity-40"
        >
          <Trash2 className="h-4 w-4" />
          Remove box{rects.length ? ` (${rects.length})` : ''}
        </button>
        <div className="flex items-center gap-2">
          <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
          <Button type="button" onClick={apply} disabled={!rects.length || saving}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Apply'}
          </Button>
        </div>
      </div>
    </div>
  );
}
